/*
 * Accessibility safety net: ensure no <img> is exposed without an accessible name.
 * Targets images that OMIT the alt attribute (WCAG 1.1.1 / F65) or use a URL as alt
 * (F30). Images intentionally marked decorative with alt="" are left untouched.
 * Covers theme markup at load AND app-injected/dynamic images (Replo, sliders,
 * lazyload) via a MutationObserver. No external data required.
 */
(function () {
  'use strict';

  var SHOP_FALLBACK = null;
  var GENERIC_FALLBACK = SHOP_FALLBACK || 'Product image';

  function clean(s) {
    return (s || '').replace(/\s+/g, ' ').trim();
  }

  // An image needs a fallback only when alt is entirely missing, or alt is a raw URL.
  function needsAlt(img) {
    if (!img.hasAttribute('alt')) return true;
    var a = img.getAttribute('alt');
    if (a && a.indexOf('://') !== -1) return true;
    return false;
  }

  // Images the page has already declared decorative, or that are too small to carry meaning.
  // These get alt="" rather than an invented description, which is what a screen reader wants.
  function isDecorative(img) {
    var role = img.getAttribute('role');
    if (role === 'presentation' || role === 'none') return true;
    if (img.closest && img.closest('[aria-hidden="true"]')) return true;
    var w = parseInt(img.getAttribute('width'), 10);
    var h = parseInt(img.getAttribute('height'), 10);
    if ((w && w <= 2) || (h && h <= 2)) return true;
    return false;
  }

  function deriveAlt(img) {
    // 1. Explicit data hook on the image
    if (img.dataset && clean(img.dataset.alt)) return clean(img.dataset.alt);

    // 2. <figure><figcaption>
    var fig = img.closest && img.closest('figure');
    if (fig) {
      var cap = fig.querySelector('figcaption');
      if (cap && clean(cap.textContent)) return clean(cap.textContent);
    }

    // 3. Inside a control that already has a name, the image is decorative — naming it again
    //    just doubles the control's accessible name ("Shop Now Shop Now").
    var control = img.closest && img.closest('a[href], button, [role="button"], [role="link"]');
    if (control && (clean(control.getAttribute('aria-label')) || clean(control.textContent))) {
      return '';
    }

    // 3b. Nearest labelled ancestor that is not itself a control (e.g. a labelled region)
    var labelled = img.closest && img.closest('[aria-label]');
    if (labelled && clean(labelled.getAttribute('aria-label'))) {
      return clean(labelled.getAttribute('aria-label'));
    }

    // 4. Product context (card / PDP custom elements)
    var prod = img.closest && img.closest('product-card-atc, product-card, product-main, [data-product-title]');
    if (prod) {
      if (prod.dataset && clean(prod.dataset.productTitle)) return clean(prod.dataset.productTitle);
      var link = prod.querySelector('a.product-card-url[aria-label]');
      if (link && clean(link.getAttribute('aria-label'))) return clean(link.getAttribute('aria-label'));
      var t = prod.querySelector('.title, h1, h2, h3');
      if (t && clean(t.textContent)) return clean(t.textContent);
    }

    // 5. Nearest section heading
    var sec = img.closest && img.closest('section, [class*="shopify-section"]');
    if (sec) {
      var h = sec.querySelector('h1, h2, h3, .title');
      if (h && clean(h.textContent)) return clean(h.textContent);
    }

    // 6/7. Last resort. Only worth a generic name on an image big enough to be content —
    //      small unnamed images are chrome, and a page-title alt on them is pure noise.
    var big = (img.naturalWidth || img.offsetWidth || parseInt(img.getAttribute('width'), 10) || 0) >= 100;
    if (!big) return '';

    var dt = clean((document.title || '').split(/[|–—\-]/)[0]);
    if (dt) return dt;

    return GENERIC_FALLBACK;
  }

  function fixImage(img) {
    if (!needsAlt(img)) return;
    if (isDecorative(img)) { img.setAttribute('alt', ''); return; }
    // deriveAlt returns '' when the image should be silent — that is a valid answer, not a miss.
    img.setAttribute('alt', deriveAlt(img));
  }

  // Known app frames, matched on id / name / src. Most specific first.
  // A generic "Embedded content" satisfies the rule but is exactly the "meaningless frame title"
  // the audit tools call out, so name the frames we can actually recognise.
  var FRAME_MAP = [
    { re: /(?:^|\.)youtube(?:-nocookie)?\.com|youtu\.be/i, title: 'YouTube video player' },
    { re: /player\.vimeo\.com/i,                           title: 'Vimeo video player' },
    { re: /google\.[a-z.]+\/maps|maps\.google/i,           title: 'Location map' },
    { re: /backinstock\.org|BIS_/i,                        title: 'Back in stock notification form' },
    { re: /klaviyo/i,                                      title: 'Email signup form' },
    { re: /okendo/i,                                       title: 'Customer reviews' },
    { re: /getrise\.ai|rise-ai|risegiftcard/i,             title: 'Gift card and store credit' },
    { re: /attentivemobile/i,                              title: 'SMS signup form' },
    { re: /frontrow/i,                                     title: 'Customer video reviews' },
    { re: /gorgias|zendesk|intercom/i,                     title: 'Customer support chat' },
    { re: /paypal|shop\.app|card-fields/i,                 title: 'Secure payment form' }
  ];

  // Frames that carry nothing for the user. Give them a truthful title and take them out of both
  // the accessibility tree and the tab order — iframes are focusable, so aria-hidden on its own
  // would create the very violation we are removing elsewhere.
  var FRAME_NONCONTENT = [
    { re: /^web-pixel-/i,                title: 'Analytics tracking frame (no content)' },
    { re: /PBarNextFrame|preview-bar/i,  title: 'Shopify preview bar (no content)' },
    { re: /doubleclick|facebook\.com\/tr|googletagmanager|google-analytics|\/pixel/i,
                                         title: 'Analytics tracking frame (no content)' }
  ];

  function frameKey(frame) {
    return [frame.id || '', frame.name || '', frame.getAttribute('src') || '',
            frame.getAttribute('data-src') || ''].join(' ');
  }

  // Iframes need a non-empty title (WCAG 4.1.2). Covers app-injected frames that ship without one.
  function fixIframe(frame) {
    var key = frameKey(frame);
    var i;

    for (i = 0; i < FRAME_NONCONTENT.length; i++) {
      if (FRAME_NONCONTENT[i].re.test(key)) {
        if (!clean(frame.getAttribute('title'))) frame.setAttribute('title', FRAME_NONCONTENT[i].title);
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('tabindex', '-1');
        return;
      }
    }

    // Apps sometimes mark their own frame aria-hidden without removing it from the tab order.
    // An iframe is focusable, so that pairing is itself a violation — complete it.
    if (frame.getAttribute('aria-hidden') === 'true' && !frame.hasAttribute('tabindex')) {
      frame.setAttribute('tabindex', '-1');
    }

    if (clean(frame.getAttribute('title'))) return;

    for (i = 0; i < FRAME_MAP.length; i++) {
      if (FRAME_MAP[i].re.test(key)) { frame.setAttribute('title', FRAME_MAP[i].title); return; }
    }

    var title = '';
    var labelled = frame.closest && frame.closest('[aria-label]');
    if (labelled) title = clean(labelled.getAttribute('aria-label'));
    if (!title) {
      var sec = frame.closest && frame.closest('section, [class*="shopify-section"]');
      var h = sec && sec.querySelector('h1, h2, h3, .title');
      if (h) title = clean(h.textContent);
    }
    if (!title) {
      var dt = clean((document.title || '').split(/[|–—\-]/)[0]);
      title = dt ? (dt + ' — embedded content') : 'Embedded content';
    }
    frame.setAttribute('title', title);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    var imgs = root.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) fixImage(imgs[i]);
    var frames = root.querySelectorAll('iframe');
    for (var j = 0; j < frames.length; j++) fixIframe(frames[j]);
  }

  function init() {
    scan(document);

    if ('MutationObserver' in window) {
      var obs = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var added = mutations[m].addedNodes;
          for (var n = 0; n < added.length; n++) {
            var node = added[n];
            if (node.nodeType !== 1) continue;
            if (node.tagName === 'IMG') {
              fixImage(node);
            } else if (node.tagName === 'IFRAME') {
              fixIframe(node);
            } else {
              scan(node);
            }
          }
        }
      });
      obs.observe(document.documentElement, { childList: true, subtree: true });
    }

    // The observer watches childList only. Apps that set aria-hidden / strip a title on a frame
    // they already inserted would slip past it, so take one more pass once everything settles.
    window.addEventListener('load', function () {
      scan(document);
      setTimeout(function () { scan(document); }, 2000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
